import { type ReactElement, type ReactNode, useCallback, useEffect, useState } from 'react';
import { type RideSupportDetail, type RideSupportPerson, type RideSupportSection, type WireMoney } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  cancelRide,
  caseRoute,
  formatMarketInstant,
  marketDisplayName,
  marketTimeZone,
  rideCancellable,
  rideStatusTone,
} from '../../src/index';
import { PersonName } from '../components/person-name';
import { RouteAnalysisPanel } from '../components/route-analysis-panel';
import { RideChatHistoryPanel } from '../components/ride-chat-history';
import { DispatchDecisionsPanel } from '../components/dispatch-decisions-panel';
import { useAdmin } from '../session';
import { IdRef } from '../components/code-ref';
import { RevealContact } from '../components/reveal-contact';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { Badge, Button, EmptyState, ListTable, Notice, PageHeader, Panel, SectionTitle, Grid, Stack, Text, HairlineRow, Spacer } from '@vello/web-theme';
import { type OpsCancelInput } from '@vello/api-client';
import { RideCancelDialog } from '../components/ride-cancel-dialog';
import { useRideMoney } from '../components/ride-money-panels';
import { LoadingBlock } from '../components/loading-block';
import { DetailRow as Row } from '../components/detail-row';
import { useVocab } from '../i18n/vocab';
import { usePolicyVersionText } from '../components/policy-version-text';
import { downloadBytes } from '../components/bi/download';

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <Panel>
      <Stack gap={4}>
        <SectionTitle>{title}</SectionTitle>
        {children}
      </Stack>
    </Panel>
  );
}

/**
 * **Corrida — visão de suporte** (W1-ADMIN, D358) over `GET /v1/ops/ride-support/rides/:rideId`: the status timeline with
 * reason codes and policy versions, the locked fare and its breakdown, the payment method when recorded, dispatch offers
 * and events, masked rider/driver cards (linking to their records), and linked cases (linking to their queues). A section
 * the server could not read says "unavailable" — never an empty list that reads as "nothing happened".
 *
 * The one action is **Cancelar corrida**, only on a live ride, through the existing audited `POST /v1/ops/rides/:id/cancel`
 * and the design-system {@link RideCancelDialog}: a stable reason code is required, and the agent's optional note is an
 * INTERNAL note sent apart — the server keeps it in the operator audit only, so it never reaches the rider, the driver or
 * the partner desk (W5-HOTFIX, D382). Every instant renders in the ride's market timezone; money from integer minor units.
 *
 * "Baixar recibo (PDF)" (a completed ride) downloads the same one-page receipt the rider gets (`GET /v1/ops/ride-support/rides/:id/receipt.pdf`); the
 * passenger's identity is on it only when the agent also holds `rider:view`.
 *
 * D438 (A-03, A-04): the ride's money (quoted → final with the revision's reason, the cash outcome) and the priced route
 * come from `useRideMoney`, which also owns "registrar recebimento" (a completed cash ride only — never beside the cancel
 * of a live ride) and "abrir chamado".
 */
export function RideDetailScreen(): ReactElement {
  const { runtime, markets } = useAdmin();
  const { params, navigate, goBack } = useRouter();
  const { toast, setRecordTitle } = useConsole();
  const { t, lang } = useT();
  const { v, fail, has, hasReason, reason } = useVocab();
  const words = usePolicyVersionText();
  const rideId = params.id ?? '';
  const [detail, setDetail] = useState<RideSupportDetail | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);
  const [receiptBusy, setReceiptBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    const res = await runtime.rideSupport.detail(rideId);
    setLoading(false);
    if (res.ok) setDetail(res.value);
    else {
      setDetail(undefined);
      setError(res.error.status === 404 || res.error.status === 403 ? t('ride.notFound') : t('ride.error', { code: fail(res.error) }));
    }
  }, [runtime, rideId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setRecordTitle(detail !== undefined ? t('ride.title', { code: detail.ride.code }) : undefined);
    return () => setRecordTitle(undefined);
  }, [detail, setRecordTitle, t]);

  // D438 (A-03, A-04): the money/cash/route panels, "registrar recebimento" and "abrir chamado".
  const rideMoney = useRideMoney({ detail, reload: load });

  if (loading && detail === undefined) {
    return <Panel><LoadingBlock /></Panel>;
  }
  if (detail === undefined) {
    return (
      <>
        <PageHeader title={t('rrep.grid.title')} actions={<Button variant="ghost" icon="arrowLeft" onClick={() => goBack('rides_grid')}>{t('ride.back')}</Button>} />
        <Notice tone="warning">{error ?? t('ride.notFound')}</Notice>
      </>
    );
  }

  const r = detail.ride;
  const tz = marketTimeZone(r.marketId, markets);
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  const money = (m: WireMoney): string => (/^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: tz ?? 'UTC', measurement: 'metric' }) : '—');
  const unavailable = (s: RideSupportSection): boolean => detail.unavailable.includes(s);
  // D384: "no permission" (`restricted`) is not "the server could not read it" (`unavailable`).
  const restricted = (s: RideSupportSection): boolean => detail.restricted?.includes(s) === true;
  const storedReasonLabel = (stored: string): string => v(has('ops.cancel_reason', stored) ? 'ops.cancel_reason' : 'ride.cancel_reason', stored);
  /** A stable reason code recorded on the ride: a decision of the platform, a cancellation reason or a refusal sentence. */
  const reasonText = (stored: string): string => {
    // The kernel records its decision codes in lower case, a few routes in upper case: both read the same words.
    for (const code of [stored, stored.toLowerCase()]) {
      for (const id of ['platform.decision_reason', 'ops.cancel_reason', 'ride.cancel_reason']) if (has(id, code)) return v(id, code);
    }
    return hasReason(stored) ? reason(stored) : v('platform.decision_reason', stored);
  };
  /** The machine detail of a dispatch event, as words: the wave, the size of the wave, the policy version and the decision. */
  const dispatchDetail = (detail: Readonly<Record<string, string | number>> | undefined): string => {
    if (detail === undefined) return '—';
    const parts: string[] = [];
    if (detail['waveNumber'] !== undefined) parts.push(t('ride.dispatch.detail.wave', { n: detail['waveNumber'] }));
    if (detail['count'] !== undefined) parts.push(t('ride.dispatch.detail.count', { n: detail['count'] }));
    if (detail['decisionReason'] !== undefined) parts.push(reasonText(String(detail['decisionReason'])));
    if (detail['policyVersion'] !== undefined) parts.push(t('ride.timeline.policy', { version: words.policy(String(detail['policyVersion'])) }));
    return parts.length > 0 ? parts.join(' · ') : '—';
  };

  const cancel = async (input: OpsCancelInput): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setCancelError(undefined);
    const res = await cancelRide(runtime, r.rideId, input);
    setBusy(false);
    if (res.ok) {
      setCancelOpen(false);
      toast({ message: t('ride.cancel.toast', { code: r.code }), tone: 'success' });
      await load();
    } else {
      setCancelError(t('ride.cancel.error', { code: fail(res.error) }));
    }
  };

  /** The ride's receipt as the PDF the rider gets (a completed ride only): fetched with the operator's session, handed to the browser as a file. */
  const downloadReceipt = async (): Promise<void> => {
    if (receiptBusy) return;
    setReceiptBusy(true);
    const res = await runtime.rideSupport.receiptPdf(r.rideId);
    setReceiptBusy(false);
    if (!res.ok) {
      toast({ message: t('ride.receipt.error', { code: fail(res.error) }), tone: 'danger' });
      return;
    }
    if (downloadBytes(res.value.fileName, res.value.bytes, res.value.contentType)) toast({ message: t('ride.receipt.toast', { code: r.code }), icon: 'download', tone: 'success' });
    else toast({ message: t('ride.receipt.blocked'), tone: 'danger' });
  };

  const person = (p: RideSupportPerson | undefined, route: 'passenger_detail' | 'driver_detail', missing: string): ReactNode =>
    p === undefined ? (
      <Text role="caption" tone="tertiary">{missing}</Text>
    ) : (
      <>
        <Row label={t('ride.person.name')} value={p.displayName ?? '—'} />
        <Row label={t('ride.person.phone')} value={<RevealContact userId={p.id} masked={p.phoneMasked} />} />
        <Row label={t('ride.person.status')} value={v('identity.user_status', p.status)} />
        <Button size="sm" variant="ghost" icon="arrowRight" onClick={() => navigate(route, { id: p.id })}>{t('ride.person.open')}</Button>
      </>
    );

  return (
    <>
      <PageHeader
        title={t('ride.title', { code: r.code })}
        subtitle={t('ride.field.timezone', { tz: tz ?? 'UTC' })}
        actions={
          <>
            <Button variant="ghost" icon="arrowLeft" onClick={() => goBack('rides_grid')}>{t('ride.back')}</Button>
            {rideMoney.actions}
            {r.status === 'completed' && (
              <Button variant="secondary" icon="download" onClick={() => void downloadReceipt()} loading={receiptBusy}>{t('ride.receipt.action')}</Button>
            )}
            {rideCancellable(r.status) && (
              <Button variant="danger" icon="ban" onClick={() => { setCancelError(undefined); setCancelOpen(true); }} disabled={busy}>{t('ride.cancel.action')}</Button>
            )}
          </>
        }
      />

      <Grid min="lg" gap={5}>
        <Section title={t('ride.section.summary')}>
<Badge tone={rideStatusTone(r.status)}>{v('ride.status', r.status)}</Badge>
          <Row label={t('ride.field.market')} value={marketDisplayName(r.marketId, markets)} />
          <Row label={t('ride.field.category')} value={v('ride.category', r.categoryCode)} />
          <Row label={t('ride.field.requestedAt')} value={when(r.requestedAt)} />
          {r.completedAt !== undefined && <Row label={t('ride.field.completedAt')} value={when(r.completedAt)} />}
          {r.cancelledAt !== undefined && <Row label={t('ride.field.cancelledAt')} value={when(r.cancelledAt)} />}
          {r.cancellationReason !== undefined && <Row label={t('ride.field.cancellationReason')} value={storedReasonLabel(r.cancellationReason)} />}
          <Row label={t('ride.field.stops')} value={String(r.stopCount)} />
          <Row label={t('ride.field.id')} value={<IdRef value={r.rideId} copy full />} />
        </Section>

        <Section title={t('ride.section.fare')}>
          <Row label={t('ride.fare.total')} value={money(detail.fare.total)} />
          <Row label={t('ride.fare.pricingVersion')} mono={false} value={<span title={detail.fare.pricingVersion}>{words.tariff(detail.fare.pricingVersion, v('ride.category', r.categoryCode))}</span>} />
          {detail.fare.surgeMultiplierBasisPoints !== undefined && <Row label={t('ride.fare.surge')} value={words.multiplier(detail.fare.surgeMultiplierBasisPoints)} />}
          {detail.fare.governedPack !== undefined && <Row label={t('ride.fare.pack')} value={`${detail.fare.governedPack.countryCode} v${detail.fare.governedPack.packVersion}`} />}
          <Text as="h3" role="label" tone="secondary">{t('ride.fare.breakdown')}</Text>
          {unavailable('fareBreakdown') || detail.fare.lines === undefined ? (
            <Notice tone="neutral">{t('ride.unavailable')}</Notice>
          ) : (
            <>
              {detail.fare.lines.map((l, i) => <Row key={`${l.code}-${i}`} label={v('fare.line_code', l.code)} value={money(l.amount)} />)}
              {detail.fare.grossTotal !== undefined && <Row label={t('ride.fare.gross')} value={money(detail.fare.grossTotal)} />}
            </>
          )}
        </Section>

        <Section title={t('ride.section.payment')}>
          {detail.payment === undefined ? (
            <Notice tone="neutral">{t('ride.unavailable')}</Notice>
          ) : (
            <>
              <Row label={t('ride.payment.method')} mono={false} value={v('ride.payment_method', detail.payment.method.toLowerCase())} />
              {detail.payment.authorization !== undefined && <Row label={t('ride.payment.authorization')} mono={false} value={t(`ride.payment.authorization.${detail.payment.authorization}`)} />}
              {detail.payment.settlement !== undefined && <Row label={t('ride.payment.settlement')} mono={false} value={t(`ride.payment.settlement.${detail.payment.settlement}`)} />}
            </>
          )}
        </Section>

        {rideMoney.panels}

        <Section title={t('ride.section.rider')}>{restricted('rider') ? <Notice tone="neutral">{t('ride.restricted')}</Notice> : unavailable('rider') ? <Notice tone="neutral">{t('ride.unavailable')}</Notice> : person(detail.rider, 'passenger_detail', t('ride.person.none'))}</Section>

        <Section title={t('ride.section.driver')}>
          {restricted('driver') ? <Notice tone="neutral">{t('ride.restricted')}</Notice> : unavailable('driver') ? <Notice tone="neutral">{t('ride.unavailable')}</Notice> : person(detail.driver, 'driver_detail', r.driverId === undefined ? t('ride.person.noDriver') : t('ride.person.none'))}
        </Section>

        <Section title={t('ride.section.cases')}>
          {unavailable('cases') ? (
            <Notice tone="neutral">{t('ride.unavailable')}</Notice>
          ) : detail.cases.length === 0 ? (
            <EmptyState title={t('ride.cases.empty')} />
          ) : (
            detail.cases.map((c) => (
              <HairlineRow key={`${c.type}-${c.id}`}>
                <Badge tone={c.type === 'safety' ? 'danger' : 'neutral'}>{v('ride.support_case_kind', c.type)}</Badge>
                <Text role="caption">{v(c.type === 'safety' ? 'safety.incident_status' : 'support.ticket_status', c.status)}{c.reasonCode !== undefined ? ` · ${reasonText(c.reasonCode)}` : ''}</Text>
                <Spacer />
                <Text role="caption" tone="tertiary" mono>{when(c.createdAt)}</Text>
                <Button size="sm" variant="ghost" onClick={() => navigate(caseRoute(c.type))}>{t('ride.cases.open')}</Button>
              </HairlineRow>
            ))
          )}
        </Section>
      </Grid>

      <Stack gap={5}>
        <section>
          <SectionTitle>{t('ride.section.timeline')}</SectionTitle>
          <ListTable
            columns={[
              { key: 'at', header: t('cases.col.when'), width: '1.2fr', render: (e: RideSupportDetail['timeline'][number]) => <span className="tnum">{formatMarketInstant(e.at, tz, lang, 'datetime-seconds')}</span> },
              { key: 'type', header: t('ride.dispatch.col.kind'), width: '1fr', render: (e: RideSupportDetail['timeline'][number]) => <Badge tone={rideStatusTone(e.type)}>{v('ride.event_type', e.type)}</Badge> },
              { key: 'actor', header: t('ride.dispatch.col.detail'), width: '1fr', render: (e: RideSupportDetail['timeline'][number]) => v('ride.actor', e.actorKind) },
              {
                key: 'reason',
                header: t('ride.cancel.reasonLabel'),
                width: '2fr',
                render: (e: RideSupportDetail['timeline'][number]) => (
                  <Text role="caption">
                    {[e.reasonCode !== undefined ? t('ride.timeline.reason', { code: reasonText(e.reasonCode) }) : undefined, e.policyVersion !== undefined ? t('ride.timeline.policy', { version: words.policy(e.policyVersion) }) : undefined].filter((x) => x !== undefined).join(' · ') || '—'}
                  </Text>
                ),
              },
            ]}
            rows={detail.timeline}
            rowKey={(e) => String(e.sequence)}
            empty={<EmptyState title={t('ride.timeline.empty')} />}
          />
        </section>

        {/* D471 (C-006, I-093): the three lines of the ride — to the pickup, the quoted estimate, what was driven. */}
        <RouteAnalysisPanel rideId={r.rideId} marketId={r.marketId} />

        {/* D473 (H-007): the messages the ride's participants and the central exchanged — only when there are any. */}
        <RideChatHistoryPanel rideId={r.rideId} marketId={r.marketId} />

        {/* D587 (ADR-0110): every dispatch decision of the ride with every driver it weighed — why this driver and not that one. */}
        <DispatchDecisionsPanel rideId={r.rideId} marketId={r.marketId} />

        <section>
          <SectionTitle>{t('ride.section.dispatch')}</SectionTitle>
          {unavailable('dispatch') ? (
            <Notice tone="neutral">{t('ride.unavailable')}</Notice>
          ) : detail.dispatch.offers.length === 0 && detail.dispatch.events.length === 0 ? (
            <Panel><EmptyState title={t('ride.dispatch.empty')} /></Panel>
          ) : (
            <Stack gap={4}>
              <ListTable
                columns={[
                  { key: 'wave', header: t('ride.dispatch.col.wave'), numeric: true, render: (o: RideSupportDetail['dispatch']['offers'][number]) => String(o.waveNumber) },
                  { key: 'driver', header: t('ride.dispatch.col.driver'), width: '1.6fr', render: (o: RideSupportDetail['dispatch']['offers'][number]) => <PersonName id={o.driverId} /> },
                  { key: 'status', header: t('ride.dispatch.col.status'), width: '1fr', render: (o: RideSupportDetail['dispatch']['offers'][number]) => <span>{v('dispatch.offer_status', o.status)}{o.rejectReason !== undefined ? ` · ${v('dispatch.offer_reject_reason', o.rejectReason)}` : ''}</span> },
                  { key: 'created', header: t('ride.dispatch.col.created'), width: '1.2fr', render: (o: RideSupportDetail['dispatch']['offers'][number]) => <span className="tnum">{formatMarketInstant(o.createdAt, tz, lang, 'datetime-seconds')}</span> },
                  { key: 'resolved', header: t('ride.dispatch.col.resolved'), width: '1.2fr', render: (o: RideSupportDetail['dispatch']['offers'][number]) => <span className="tnum">{formatMarketInstant(o.resolvedAt, tz, lang, 'datetime-seconds')}</span> },
                ]}
                rows={detail.dispatch.offers}
                rowKey={(o) => o.offerId}
                countLabel={t('ride.dispatch.offers')}
                empty={<EmptyState title={t('ride.dispatch.empty')} />}
              />
              <ListTable
                columns={[
                  { key: 'at', header: t('cases.col.when'), width: '1.2fr', render: (e: RideSupportDetail['dispatch']['events'][number]) => <span className="tnum">{formatMarketInstant(e.at, tz, lang, 'datetime-seconds')}</span> },
                  { key: 'kind', header: t('ride.dispatch.col.kind'), width: '1fr', render: (e: RideSupportDetail['dispatch']['events'][number]) => v('dispatch.event_kind', e.kind) },
                  { key: 'detail', header: t('ride.dispatch.col.detail'), width: '2.4fr', render: (e: RideSupportDetail['dispatch']['events'][number]) => <Text role="caption" mono>{dispatchDetail(e.detail)}</Text> },
                ]}
                rows={detail.dispatch.events}
                rowKey={(e) => `${e.at}-${e.kind}`}
                countLabel={t('ride.dispatch.events')}
                empty={<EmptyState title={t('ride.dispatch.empty')} />}
              />
            </Stack>
          )}
        </section>
      </Stack>

      <RideCancelDialog
        open={cancelOpen}
        rideLabel={r.code}
        busy={busy}
        error={cancelError}
        onCancel={() => setCancelOpen(false)}
        onConfirm={(input) => void cancel(input)}
      />
      {rideMoney.dialogs}
    </>
  );
}
